import { AxiosError } from "axios"

interface ApiErrorResponse {
  detail?: string | Record<string, unknown>
  message?: string
  error?: string
  code?: string
  existing_pipeline_lead_id?: string
  pipeline_id?: string
  lead_id?: string
  missing_fields?: string[]
  qualification_form_id?: string
  qualification_scope?: string
  stage_id?: string
}

export interface QualificationMissingFieldsError {
  missing_fields: string[]
  qualification_form_id: string | null
  qualification_scope: string | null
  stage_id: string | null
  message: string
}

/**
 * Detect the qualification-required-fields rejection emitted by the move-stage
 * guardrail. The handler is BusinessRuleException with code
 * `qualification_required_fields_missing` - returns the structured payload so
 * callers can open the qualification dialog with the right context instead of
 * showing a generic toast.
 */
export function extractQualificationMissingFields(
  error: unknown
): QualificationMissingFieldsError | null {
  if (!(error instanceof AxiosError)) return null
  if (error.response?.status !== 400) return null
  const data = error.response.data as ApiErrorResponse | undefined
  if (!data || data.code !== "qualification_required_fields_missing") return null
  if (!data.missing_fields || data.missing_fields.length === 0) return null
  return {
    missing_fields: data.missing_fields,
    qualification_form_id: data.qualification_form_id ?? null,
    qualification_scope: data.qualification_scope ?? null,
    stage_id: data.stage_id ?? null,
    message: typeof data.detail === "string" ? data.detail : "Qualificação incompleta",
  }
}

export interface ActivePipelineLeadConflict {
  existing_pipeline_lead_id: string
  pipeline_id: string
  lead_id: string | null
  message: string
}

/**
 * Detect HTTP 409 ACTIVE_PIPELINE_LEAD_EXISTS responses from the backend.
 * Returns the conflict payload when present so callers can offer "open the
 * existing card" instead of showing a generic error.
 */
export function extractActivePipelineLeadConflict(
  error: unknown
): ActivePipelineLeadConflict | null {
  if (!(error instanceof AxiosError)) return null
  if (error.response?.status !== 409) return null
  const data = error.response.data as ApiErrorResponse | undefined
  if (!data || data.code !== "ACTIVE_PIPELINE_LEAD_EXISTS") return null
  if (!data.existing_pipeline_lead_id || !data.pipeline_id) return null
  return {
    existing_pipeline_lead_id: data.existing_pipeline_lead_id,
    pipeline_id: data.pipeline_id,
    lead_id: data.lead_id ?? null,
    message:
      typeof data.detail === "string"
        ? data.detail
        : "Já existe um card ativo deste contato neste funil",
  }
}

/**
 * Detecta o gate "plugin não está ativo" do backend (HTTP 400 com detail
 * contendo "não está ativo"), emitido por require_plugin_active nas features
 * baseadas em plugin. Permite que a tela mostre um CTA inline de "ativar plugin"
 * em vez de um erro genérico.
 */
export function isPluginInactiveError(error: unknown): boolean {
  if (!(error instanceof AxiosError)) return false
  if (error.response?.status !== 400) return false
  const data = error.response.data as ApiErrorResponse | undefined
  return typeof data?.detail === "string" && data.detail.includes("não está ativo")
}

/**
 * Extrai a mensagem de erro de uma resposta da API ou de um erro geral
 * @param error - O erro a ser processado (pode ser AxiosError ou Error)
 * @param fallbackMessage - Mensagem de fallback caso não seja possível extrair uma mensagem do erro
 * @returns A mensagem de erro extraída
 */
export function extractErrorMessage(error: unknown, fallbackMessage: string): string {
  // Se for um erro do Axios
  if (error instanceof AxiosError) {
    // Tenta extrair a mensagem do response.data
    const responseData = error.response?.data as ApiErrorResponse | undefined

    if (responseData) {
      // detail pode ser string ou objeto (ex: FastAPI HTTPException com dict)
      const detail = responseData.detail
      if (typeof detail === "string") return detail
      if (detail && typeof detail === "object") {
        const msg =
          (detail as Record<string, unknown>).error ?? (detail as Record<string, unknown>).message
        if (typeof msg === "string") return msg
      }
      return responseData.message || responseData.error || fallbackMessage
    }

    // Sem response: erro de rede / timeout. Evita expor a string crua
    // "Network Error" do axios para o usuário final.
    if (error.code === "ERR_NETWORK") {
      return "Sem resposta do servidor. Verifique sua conexão e tente novamente."
    }
    if (error.code === "ECONNABORTED") {
      return "Tempo de resposta esgotado. Tente novamente."
    }
    return fallbackMessage
  }

  // Se for um Error padrão
  if (error instanceof Error) {
    return error.message || fallbackMessage
  }

  // Se for uma string
  if (typeof error === "string") {
    return error
  }

  // Fallback para o caso de não conseguir extrair uma mensagem
  return fallbackMessage
}
